import type {
	MotornetAccessoriesResponse,
	MotornetAccessory,
	MotornetAutosuggestMatch,
	MotornetAutosuggestResponse,
	MotornetCodeLabel,
	MotornetIdLabel,
	MotornetImage,
	MotornetMakeWithKind,
	MotornetModel,
	MotornetModelSummary,
	MotornetPriceDetails,
	MotornetVehicleKind,
	MotornetVehicleResponse,
	MotornetVersionSummary,
} from 'src/motornet/types';
import type {
	AutovistaCodeName,
	AutovistaEquipment,
	AutovistaHistoricalPrice,
	AutovistaImage,
	AutovistaMake,
	AutovistaModel,
	AutovistaPollutionNorm,
	AutovistaProductionPeriod,
	AutovistaVehicleKind,
	AutovistaVehiclePayload,
	AutovistaVehicleType,
} from './types';


const DEFAULT_ES_VAT_RATE = 21;
export const SYNTHETIC_MANUFACTURE_CODE_PREFIX = 'AV-';

const str = (v: string | null | undefined): string => v ?? '';

const measure = (v: number | null | undefined): number | null =>
	v === null || v === undefined || v === 0 ? null : v;

const numOr = (v: number | null | undefined, fallback: number): number =>
	v === null || v === undefined ? fallback : v;

/**
 * Autovista ids (`NationalVehicleCode`, equipment `Code`, `MakeCode`) are `long`
 * on the wire and typed as opaque strings — some exceed what a JS number holds
 * safely. Motornet's `idAccessorio` / `idDizionario` are numbers and are used
 * for de-duplication, so we need a stable, collision-resistant number. Parse
 * when it is a safe integer; otherwise hash (FNV-1a, 32-bit).
 */
const stableId = (code: string | null | undefined): number => {
	if (!code) return 0;
	const parsed = Number(code);
	if (Number.isSafeInteger(parsed) && parsed > 0) return parsed;
	let hash = 0x811c9dc5;
	for (let i = 0; i < code.length; i++) {
		hash ^= code.charCodeAt(i);
		hash = Math.imul(hash, 0x01000193);
	}
	return hash >>> 0;
};

/**
 * `ETGlookupType` → Motornet's code/label pair. Motornet keeps a single label
 * and it is the LONG one, so `nameEx` wins over `name`.
 */
const codeLabel = (v: AutovistaCodeName | null | undefined): MotornetCodeLabel => ({
	codice: str(v?.code),
	descrizione: str(v?.nameEx ?? v?.name),
});

const idLabel = (id: number, descrizione: string): MotornetIdLabel => ({
	id,
	descrizione,
});

/**
 * Dates arrive from the proxy already flattened to `YYYY-MM-DD`, but the
 * underlying SOAP layer uses three serializations (`06`, `--06`, `--11--`;
 * `01`, `1`, `---12`). Strip stray separators defensively before handing the
 * string on — every Motornet date field is a plain string, so we never parse.
 */
const dateStr = (v: string | null | undefined): string => {
	if (!v) return '';
	return v.replace(/^-+/, '').replace(/-+$/, '');
};

export const productionYearMonth = (
	v: AutovistaProductionPeriod | undefined
): { year: number; month: number } | null => {
	if (!v) return null;
	if (typeof v === 'string') {
		const m = /^(\d{4})-(\d{1,2})/.exec(v.trim());
		return m ? { year: Number(m[1]), month: Number(m[2]) } : null;
	}
	return v.year == null ? null : { year: v.year, month: v.month ?? 1 };
};

/** `YYYY-MM` — forma con cui i periodi entrano nel modello Motornet. */
export const productionPeriodStr = (
	v: AutovistaProductionPeriod | undefined
): string => {
	const ym = productionYearMonth(v);
	return ym ? `${ym.year}-${String(ym.month).padStart(2, '0')}` : '';
};

const productionPeriodOrNull = (
	v: AutovistaProductionPeriod | undefined
): string | null => productionPeriodStr(v) || null;

const dedupeBy = <T,>(items: T[], key: (item: T) => string): T[] => {
	const seen = new Set<string>();
	const out: T[] = [];
	for (const item of items) {
		const k = key(item);
		if (seen.has(k)) continue;
		seen.add(k);
		out.push(item);
	}
	return out;
};

/**
 * Scorporo IVA.
 */
const netFromGross = (gross: number, vatRate: number | null | undefined): number =>
	Math.round((gross / (1 + numOr(vatRate, DEFAULT_ES_VAT_RATE) / 100)) * 100) /
	100;

/** Applicazione IVA. */
const grossFromNet = (net: number, vatRate: number | null | undefined): number =>
	Math.round(net * (1 + numOr(vatRate, DEFAULT_ES_VAT_RATE) / 100) * 100) / 100;

export const toMotornetKind = (
	kind: AutovistaVehicleKind | null | undefined
): MotornetVehicleKind => (kind === 'vcom' ? 'vcom' : 'auto');

export const toAutovistaKind = (
	kind: MotornetVehicleKind | null | undefined
): AutovistaVehicleKind | undefined => {
	if (kind === 'vcom') return 'vcom';
	if (kind === 'auto') return 'auto';
	return undefined;
};


export const makeToMotornet = (make: AutovistaMake): MotornetMakeWithKind => ({
	acronimo: make.makeCode,
	nome: str(make.name),
	logo: null,
	vehicleKind: toMotornetKind(make.vehicle_kind),
});

export const makesToMotornet = (
	makes: AutovistaMake[] | null | undefined
): MotornetMakeWithKind[] =>
	dedupeBy(
		(makes ?? []).map(makeToMotornet),
		(m) => `${m.acronimo}|${m.vehicleKind}`
	);

export const modelToMotornet = (model: AutovistaModel): MotornetModelSummary => {
	const gammaModello: MotornetCodeLabel = {
		codice: model.modelCode,
		descrizione: str(model.name),
	};
	return {
		marca: model.makeCode,
		gammaModello,
		inizioProduzione: productionPeriodOrNull(model.productionStart),
		fineProduzione: productionPeriodOrNull(model.productionEnd),
		gruppoStorico: gammaModello,
		serieGamma: gammaModello,
		codDescModello: gammaModello,
		inizioCommercializzazione: productionPeriodOrNull(model.productionStart),
		fineCommercializzazione: productionPeriodOrNull(model.productionEnd),
		modello: str(model.nameExtended ?? model.name),
		foto: null,
		prezzoMinimo: null,
		modelloBreveCarrozzeria: null,
		vehicleKind: toMotornetKind(model.vehicle_kind),
	};
};

export const modelsToMotornet = (
	models: AutovistaModel[] | null | undefined
): MotornetModelSummary[] => (models ?? []).map(modelToMotornet);

export const versionToMotornet = (
	version: AutovistaVehicleType
): MotornetVersionSummary => ({
	codiceEurotax: version.nationalVehicleCode,
	codiceMotornet: '',
	da: productionPeriodOrNull(version.productionStart),
	a: productionPeriodOrNull(version.productionEnd),
	inizioProduzione: productionPeriodOrNull(version.productionStart),
	fineProduzione: productionPeriodOrNull(version.productionEnd),
	nome: str(version.name ?? version.trimLineName),
	marca: {
		acronimo: version.makeCode,
		nome: str(version.makeName),
		logo: null,
	},
	vehicleKind: toMotornetKind(version.vehicle_kind),
	versione: str(version.trimLineName ?? version.name),
	codiceCostruttore: str(version.manufactureCode),
	codiciCostruttoreAlternativi: [],
});


export const versionsToMotornet = (
	versions: AutovistaVehicleType[] | null | undefined
): MotornetVersionSummary[] =>
	dedupeBy((versions ?? []).map(versionToMotornet), (v) => v.codiceEurotax);


const versionToMatch = (v: AutovistaVehicleType): MotornetAutosuggestMatch => ({
	codiceEurotax: v.nationalVehicleCode,
	codiceMotornet: '',
	da: productionPeriodStr(v.productionStart),
	a: productionPeriodOrNull(v.productionEnd),
	inizioProduzione: productionPeriodStr(v.productionStart),
	fineProduzione: productionPeriodOrNull(v.productionEnd),
	idModelloBreveCarrozzeria: stableId(v.modelCode),
	versioneBase: idLabel(stableId(v.nationalVehicleCode), str(v.name)),
	descrizioneModelloBreveCarrozzeria: str(v.modelName),
	codiceModello: v.modelCode,
	versione: str(v.name ?? v.trimLineName),
	tipo: str(v.bodyStyle?.nameEx),
	tipoVeicolo: v.vehicleTypeName,
	passo: numOr(measure(v.wheelbase), 0),
	prezzoVendita: numOr(v.newPrice?.amount, 0),
	porte: numOr(v.doorsNumber, 0),
	codiceCostruttore: str(v.manufactureCode),
	codiciCostruttoreAlternativi: [],
	marca: {
		acronimo: v.makeCode,
		nome: str(v.makeName),
		logo: null,
	},
	vehicleKind: toMotornetKind(v.vehicle_kind),
});

export const autosuggestToMotornet = (
	matches: AutovistaVehicleType[] | null | undefined,
	manufactureCode: string
): MotornetAutosuggestResponse => {
	const list = (matches ?? []).map(versionToMatch);
	return {
		manufactureCode,
		triedVehicleKinds: Array.from(new Set(list.map((m) => m.vehicleKind))),
		count: list.length,
		matches: list,
		errors: {},
	};
};

const EMPTY_ACCESSORIES = (): MotornetAccessoriesResponse => ({
	serie: [],
	vernici: [],
	optionals: [],
	pacchetti: [],
	accessoriVinNonCorrispondenti: [],
});

/**
 * Autovista non ha i gruppi Motornet: la classificazione utile sta in ESACO.
 * Vernici e tappezzerie hanno un sottogruppo dedicato ed e' da li' che i select
 * colore del form pescano — via `macrogruppo`, il campo su cui `motornetToForm`
 * riconosce "Vernici" e "Interni".
 *
 * Solo i `subGroupCode` numerici: `subGroupDescription` e' localizzato via
 * `ISOlanguageCode` e un match sul testo si rompe al cambio di lingua.
 */
const ESACO_PAINT_SUBGROUPS = new Set(['760059', '760116', '760138']);
/** 760134 = allestimento interni in pelle (plancia, pannelli porta). */
const ESACO_UPHOLSTERY_SUBGROUPS = new Set(['760066', '760118', '760134']);

/**
 * Un accessorio appartiene alla famiglia se almeno un nodo ESACO la nomina:
 * `ESACOdata` e' ripetibile e una voce puo' citarne decine. I bundle restano
 * fuori perche' i `PACKAGE` sono gia' instradati altrove; i `PACKAGE_ITEM`
 * invece sono colori veri e vanno tenuti anche quando portano nodi estranei.
 */
const esacoFamily = (
	equipment: AutovistaEquipment,
	codes: Set<string>
): boolean =>
	(equipment.esaco ?? []).some((node) => {
		const code = node.subGroupCode?.trim();
		return !!code && codes.has(code);
	});

const isPaintEquipment = (equipment: AutovistaEquipment): boolean =>
	esacoFamily(equipment, ESACO_PAINT_SUBGROUPS);

const isInteriorEquipment = (equipment: AutovistaEquipment): boolean =>
	esacoFamily(equipment, ESACO_UPHOLSTERY_SUBGROUPS);

const isPackage = (equipment: AutovistaEquipment): boolean =>
	equipment.equipmentPackageType === 'PACKAGE';

/**
 * Compreso nel prezzo del veicolo
 */
const isStandardEquipment = (equipment: AutovistaEquipment): boolean =>
	equipment.equipmentType === 'SERIAL' ||
	equipment.equipmentType === 'OPTIONAL_FREE_OF_CHARGE' ||
	equipment.equipmentType === 'OPTIONAL_INCLUDED_IN_QUOTATION';

/**
 * Il codice mostrato e salvato: quello costruttore quando c'e' (e' quello che
 * il concessionario riconosce), altrimenti l'id Autovista.
 */
const equipmentCode = (equipment: AutovistaEquipment): string =>
	equipment.manufactureCode?.trim() || equipment.code;

const equipmentToAccessory = (
	equipment: AutovistaEquipment,
	macrogruppo: string,
	vatRate: number | null | undefined
): MotornetAccessory => {
	const id = stableId(equipment.code);
	const esaco = equipment.esaco?.[0];
	const descrizione = str(equipment.longName ?? equipment.shortName);

	const net = equipment.newPrice?.amount ?? null;
	// const gross = net === null ? null : grossFromNet(net, vatRate);
	const gross = net // @todo - check autovista logic

	return {
		codice: equipmentCode(equipment),
		prezzo: gross,
		idGruppo: 0,
		versione: '',
		dataListino: dateStr(equipment.adoptationDate),
		descrizione,
		macrogruppo,
		codiceGruppo: str(equipment.groupCode),
		idAccessorio: id,
		idDizionario: id,
		svalutazione: null,
		accessorioVin: null,
		idMacrogruppo: 0,
		prezzoListino: gross,
		prezzoListinoNetto: net ?? 0,
		codiceMotornet: null,
		formulaVincoli: [],
		idDisponibilita: 0,
		descrizioneBreve: str(equipment.shortName ?? equipment.longName),
		codiceMacrogruppo: '',
		descrizioneGruppo: '',
		formulaEsclusione: '',
		formulaInclusione: '',
		idEquipaggiamento: 0,
		codiceNormalizzato: str(equipment.code),
		idDizionarioGruppo: 0,
		codiceDisponibilita: '',
		descrizioneMotornet: null,
		idAccessorioEsterno: 0,
		descrizioneNormalizzata: str(esaco?.description ?? equipment.longName),
		descrizioneDisponibilita: '',
		formulaVincoliStrutturata: null,
		descrizioneEquipaggiamento: str(esaco?.mainGroupDescription),
		formulaEsclusioneStrutturata: null,
		formulaInclusioneStrutturata: null,
		formulaEsclusioneNormalizzata: null,
		formulaInclusioneNormalizzata: null,
		prevedeEsclusioneLogicaGruppo: false,
	};
};

/**
 * `equipment[]` → i quattro bucket Motornet. I pacchetti restano a parte anche
 * quando toccano vernici o tappezzeria: valgono come voce unica, non come
 * scelta di colore.
 */
export const equipmentToAccessories = (
	equipment: AutovistaEquipment[] | null | undefined,
	vatRate?: number | null
): MotornetAccessoriesResponse => {
	const out = EMPTY_ACCESSORIES();

	for (const item of equipment ?? []) {
		if (!item?.code) continue;

		if (isPackage(item)) {
			out.pacchetti!.push(equipmentToAccessory(item, 'Pacchetti', vatRate));
			continue;
		}

		const paint = isPaintEquipment(item);
		const interior = isInteriorEquipment(item);
		const macrogruppo = paint ? 'Vernici' : interior ? 'Interni' : 'Optional';

		const accessory = equipmentToAccessory(item, macrogruppo, vatRate);
		if (isStandardEquipment(item)) out.serie!.push(accessory);
		else if (paint) out.vernici!.push(accessory);
		else out.optionals!.push(accessory);
	}

	return out;
};

const pickPricePeriod = (
	history: AutovistaHistoricalPrice[] | null | undefined,
	current: AutovistaHistoricalPrice | null,
	reqDate: string | null
): AutovistaHistoricalPrice | null => {
	if (current) return current;
	const sorted = [...(history ?? [])].sort((a, b) =>
		str(a.validFrom).localeCompare(str(b.validFrom))
	);
	if (!sorted.length) return null;
	if (!reqDate) return sorted[sorted.length - 1];

	const containing = sorted.find(
		(p) => str(p.validFrom) <= reqDate && (!p.validTo || reqDate <= str(p.validTo))
	);
	if (containing) return containing;

	const started = sorted.filter((p) => str(p.validFrom) <= reqDate);
	return started.length ? started[started.length - 1] : sorted[sorted.length - 1];
};

const priceToMotornet = (
	price: AutovistaHistoricalPrice | null
): MotornetPriceDetails => {
	const gross = price?.listPrice ?? 0;
	return {
		dataListino: dateStr(price?.validFrom),
		messaSustrada: numOr(price?.deliveryFee, 0),
		prezzoListino: gross,
		prezzoListinoNetto: netFromGross(gross, price?.valueAddedTaxRate),
	};
};

const imagesToMotornet = (
	images: AutovistaImage[] | null | undefined
): MotornetImage[] =>
	[...(images ?? [])]
		.sort((a, b) => numOr(b.width, 0) - numOr(a.width, 0))
		.map((img) => ({
			url: img.url,
			risoluzione: img.width && img.height ? `${img.width}x${img.height}` : '',
			codiceVisuale: str(img.viewCode),
			codiceFotografia: str(img.id),
			descrizioneVisuale: str(img.viewDescription),
		}));

const emptyModel = (): MotornetModel => {
	const emptyLabel = (): MotornetCodeLabel => ({ codice: '', descrizione: '' });
	return {
		hc: '',
		hp: 0,
		kw: 0,
		nox: '',
		euro: '',
		foto: null,
		peso: 0,
		pm10: null,
		tipo: emptyLabel(),
		wltp: null,
		freni: null,
		guado: null,
		marca: { logo: null, nome: '', acronimo: '' },
		passo: 0,
		porte: 0,
		posti: 0,
		cambio: emptyLabel(),
		codVel: '',
		coppia: '',
		traino: 0,
		volumi: '',
		altezza: 0,
		modello: '',
		portata: null,
		ridotte: false,
		valvole: 0,
		cilindri: '',
		immagine: '',
		postiMax: null,
		segmento: emptyLabel(),
		tipoCons: '',
		trazione: emptyLabel(),
		velocita: 0,
		capSerbKg: null,
		categoria: null,
		emissUrbe: 0,
		larghezza: 0,
		lunghezza: 0,
		paeseProd: '',
		pesoVuoto: '',
		sospPneum: 0,
		tipoGuida: '',
		bagagliaio: '',
		cilindrata: 0,
		garanziaKm: null,
		nomeCambio: '',
		nomeMotore: '',
		numeroGiri: 0,
		serieGamma: emptyLabel(),
		tipoMotore: '',
		dataListino: '',
		pendenzaMax: null,
		pesoPotenza: '',
		allestimento: '',
		architettura: emptyLabel(),
		capSerbLitri: 0,
		codiceMotore: '',
		consumoMedio: null,
		coppiaIbrido: '',
		coppiaTotale: '',
		emissioniCo2: '',
		gammaModello: emptyLabel(),
		indiceCarico: '',
		massaPCarico: '',
		motoreIbrido: null,
		neoPatentati: false,
		nomeTrazione: null,
		prezzoMinimo: null,
		tipoBatteria: '',
		accelerazione: 0,
		alimentazione: emptyLabel(),
		altezzaMinima: null,
		cavalliIbrido: null,
		cavalliTotale: null,
		codiceEurotax: '',
		consumoUrbano: null,
		emissExtraurb: 0,
		garanziaTempo: null,
		gruppoStorico: emptyLabel(),
		potenzaIbrido: null,
		potenzaTotale: null,
		prezzoListino: 0,
		tempiGiacenza: 0,
		accessoriSerie: [],
		autonomiaMedia: null,
		cavalliFiscali: 0,
		codDescModello: emptyLabel(),
		codiceMotornet: '',
		fineProduzione: null,
		ricaricaVeloce: '',
		codiceCategoria: null,
		equipaggiamento: '',
		motoreElettrico: null,
		nomeCarrozzeria: null,
		prezzoAccessori: null,
		siglaCilindrata: '',
		tipologiaMotore: '',
		autonomiaMassima: null,
		descrizioneBreve: '',
		descrizioneMarce: '',
		inizioProduzione: '',
		nomeAllestimento: '',
		nomeMotoreIbrido: '',
		numeroGiriIbrido: null,
		numeroGiriTotale: null,
		ricaricaStandard: '',
		allestimentoBreve: '',
		codiceCostruttore: '',
		descrizioneMotore: '',
		accessoriOpzionali: [],
		consumoExtraurbano: null,
		cavalliElettricoMax: null,
		nomeMotoreElettrico: null,
		pneumaticiAnteriori: '',
		potenzaElettricoMax: null,
		descrizioneCategoria: null,
		pneumaticiPosteriori: '',
		capacitaNettaBatteria: null,
		codiceMotornetUnivoco: '',
		tipologiaMotoreIbrido: '',
		descrizioneModelloBreve: '',
		fineCommercializzazione: null,
		modelloBreveCarrozzeria: idLabel(0, ''),
		capacitaNominaleBatteria: null,
		cavalliElettricoBoostMax: null,
		descrizioneArricchimento: '',
		potenzaElettricoBoostMax: null,
		tipologiaMotoreElettrico: null,
		inizioCommercializzazione: '',
	};
};

/** CV from kW when Autovista supplies only one of the two. */
const HP_PER_KW = 1.35962;

const pickPollutionNorm = (
	norms: AutovistaPollutionNorm[] | undefined,
	reqDate: string | null
): string => {
	const sorted = [...(norms ?? [])].sort((a, b) =>
		str(a.validFrom).localeCompare(str(b.validFrom))
	);
	if (!sorted.length) return '';
	if (!reqDate) return str(sorted[sorted.length - 1].textLong);
	const eligible = sorted.filter((n) => str(n.validFrom) <= reqDate);
	return str((eligible.length ? eligible[eligible.length - 1] : sorted[0]).textLong);
};

/**
 * Il codice costruttore del veicolo, sintetizzandolo quando Autovista non ne
 * fornisce uno — sul mercato ES manca quasi sempre.
 *
 * Il codice sintetico e' `AV-<makeCode>-<modelCode>-<nationalVehicleCode>`.
 */
export const manufactureCodeOf = (payload: AutovistaVehiclePayload): string => {
	const real = str(payload.manufactureCode ?? payload.type?.manufactureCode);
	if (real) return real;

	const identity = payload.nationalVehicleCode || payload.vehicle_code;
	const parts = [payload.type?.makeCode, payload.type?.modelCode, identity]
		.map((part) => str(part).trim())
		.filter(Boolean);

	return `${SYNTHETIC_MANUFACTURE_CODE_PREFIX}${parts.join('-')}`;
};

export interface VehicleNormalizeOptions {
	referenceDate?: string | null;
	year?: number;
	month?: number;
}

export const vehicleToMotornet = (
	payload: AutovistaVehiclePayload,
	{ referenceDate = null, year, month }: VehicleNormalizeOptions = {}
): MotornetVehicleResponse => {
	const type = payload.type;
	const tech = payload.technicalSpecification;
	const manufactureCode = manufactureCodeOf(payload);

	// const price = pickPricePeriod(
	// 	payload.priceHistory,
	// 	payload.currentPrice,
	// 	referenceDate
	// );
	const currentPriceDetails = priceToMotornet(payload.currentPrice);
	const images = imagesToMotornet(payload.images);

	const kw = tech?.engine.power.kw ?? type?.power?.kw ?? null;
	const hpRaw = tech?.engine.power.hp ?? type?.power?.hp ?? null;
	const hp = hpRaw ?? (kw === null ? null : Math.round(kw * HP_PER_KW));

	const gearBoxType = tech?.gearBox.type ?? type?.gearBoxType ?? null;
	const gears = tech?.gearBox.numberOfGear ?? type?.gearsNumber ?? null;
	const torque = tech?.engine.torque ?? null;

	const co2 =
		tech?.exhaust.co2emission ?? tech?.exhaustWltp.co2emissionCombined.max ?? null;

	const model: MotornetModel = {
		...emptyModel(),
		marca: {
			acronimo: str(type?.makeCode),
			nome: str(type?.makeName),
			logo: null,
		},
		modello: str(type?.modelName),
		gammaModello: {
			codice: str(type?.modelCode),
			descrizione: str(type?.modelName),
		},
		codDescModello: {
			codice: str(type?.modelCode),
			descrizione: str(type?.modelName),
		},
		allestimento: str(type?.trimLineName),
		allestimentoBreve: str(type?.trimLineName),
		nomeAllestimento: str(type?.name),
		codiceEurotax: payload.nationalVehicleCode,
		codiceCostruttore: manufactureCode,
		// Motornet-only concept — no Autovista equivalent.
		codiceMotornet: '',
		prezzoListino: currentPriceDetails.prezzoListino,
		dataListino: currentPriceDetails.dataListino,
		immagine: images[0]?.url ?? '',
		foto: images[0]?.url ?? null,
		inizioProduzione: productionPeriodStr(type?.productionStart),
		fineProduzione: productionPeriodOrNull(type?.productionEnd),
		inizioCommercializzazione: productionPeriodStr(type?.productionStart),
		fineCommercializzazione: productionPeriodOrNull(type?.productionEnd),

		alimentazione: codeLabel(tech?.fuel.type ?? type?.fuel),
		cambio: codeLabel(gearBoxType),
		nomeCambio: gears ? `${str(gearBoxType?.nameEx)} ${gears}`.trim() : '',
		descrizioneMarce: gears ? String(gears) : '',
		trazione: codeLabel(type?.driveType),
		nomeTrazione: type?.driveType?.nameEx ?? null,
		kw: numOr(kw, 0),
		potenzaTotale: kw,
		hp: numOr(hp, 0),
		cavalliTotale: hp,
		cilindrata: numOr(tech?.engine.displacement ?? type?.displacement, 0),
		cilindri: String(tech?.engine.numberOfCylinder ?? type?.cylindersNumber ?? ''),
		valvole: numOr(tech?.engine.valvesPerCylinder ?? type?.valvesPerCylinder, 0),
		coppia: torque === null ? '' : String(torque),
		coppiaTotale: torque === null ? '' : String(torque),
		nomeMotore: str(tech?.engineExtended.engineTypeLong),
		descrizioneMotore: str(tech?.engineExtended.engineTypeLong),
		tipoMotore: str(tech?.engineExtended.engineTypeShort),
		cavalliFiscali: numOr(tech?.taxHorsePower ?? type?.taxHorsePower, 0),

		porte: numOr(type?.doorsNumber, 0),
		posti: numOr(tech?.capacity.numberOfSeat ?? type?.seatsNumber, 0),
		tipo: codeLabel(type?.bodyStyle),
		nomeCarrozzeria: type?.bodyStyle?.nameEx ?? null,
		segmento: codeLabel(type?.marketSegment),
		passo: numOr(measure(tech?.dimension.wheelbase ?? type?.wheelbase), 0),
		altezza: numOr(measure(tech?.dimension.height), 0),
		larghezza: numOr(measure(tech?.dimension.width), 0),
		lunghezza: numOr(measure(tech?.dimension.length), 0),
		peso: numOr(measure(tech?.weight.total ?? type?.totalWeight), 0),
		pesoVuoto: String(measure(tech?.weight.curb ?? type?.curbWeight) ?? ''),
		portata: measure(tech?.weight.payLoad ?? type?.payLoad),
		capSerbLitri: numOr(measure(tech?.capacity.fuelTank), 0),
		bagagliaio: String(measure(tech?.capacity.minimumTrunkVolume) ?? ''),

		velocita: numOr(measure(tech?.performance.topSpeed), 0),
		accelerazione: numOr(tech?.performance.accelerationTime, 0),
		consumoUrbano: measure(tech?.consumption.urban),
		consumoExtraurbano: measure(tech?.consumption.overLand),
		consumoMedio:
			measure(tech?.consumption.mixed) ??
			measure(tech?.consumptionWltp.combined.max),
		emissioniCo2: measure(co2) === null ? '' : String(co2),
		euro: pickPollutionNorm(tech?.pollutionNorms, referenceDate),
		autonomiaMedia: measure(tech?.electricRange),
		autonomiaMassima: measure(tech?.electricRange),
		pneumaticiAnteriori: str(tech?.wheelInformation.frontTyreNotation),
		pneumaticiPosteriori: str(tech?.wheelInformation.rearTyreNotation),
	};

	return {
		year: year ?? 0,
		month: month ?? 0,
		images,
		details: {
			modello: model,
			accessiDisponibili: 0,
		},
		accessories: equipmentToAccessories(
			payload.equipment,
			payload.currentPrice?.valueAddedTaxRate
		),
		eurotaxCode: payload.nationalVehicleCode,
		vehicleKind: toMotornetKind(payload.vehicle_kind),
		priceListDates: (payload.priceHistory ?? []).map(priceToMotornet),
		manufactureCode,
		currentPriceDetails,
	};
};
